import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/lib/Button';
import Tooltip from '@material-ui/core/Tooltip';

import * as filterTypes from './filterTypes';
import DateFilter from './DateFilter';
import { RANGE_FILTER_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import TextsFilter from './TextsFilter';
import RadioFilter from './RadioFilter';
import { Checkbox } from '@silverfort/sf-components';

const Filter = (props) => {
    const { id: filterId, filterType, filterConfig, value, notAllowedRangeTypes } = props.filter;
    const { openOnTop } = props;
    const [tooltipStatus, setTooltipStatus] = useState({});
    const [isExcluded, setIsExcluded] = useState(props.filter.isExcluded);
    const [checkedValues, setCheckedValues] = useState(new Set(value));
    const [textValue, setTextValue] = useState(value);
    const [textsValue, setTextsValue] = useState(value);
    const [dateRange, setDateRange] = useState(value);
    const [radioValue, setRadioValue] = useState(value && value[0]);
    const [checkboxSearchText, setCheckboxSearchText] = useState('');

    useEffect(() => {
        if (filterType === filterTypes.SELECT) {
            filterConfig.options.forEach((selectOption) => {
                if (selectOption.optionRef) {
                    const optionId = selectOption.value;
                    setTooltipStatus((prevState) => {
                        prevState[optionId] =
                            selectOption.optionRef.current.scrollWidth > selectOption.optionRef.current.clientWidth;
                        return prevState;
                    });
                }
            });
        }
    }, [filterType, filterConfig]);

    const onCheckChanged = (value, checked) => {
        const newCheckedValues = new Set(checkedValues);
        if (checked) {
            newCheckedValues.add(value);
        } else {
            newCheckedValues.delete(value);
        }

        setCheckedValues(newCheckedValues);
    };

    const onTextChanged = (event) => {
        setTextValue(event.target.value);
    };

    const onDatesChanged = (rangeType, startDate, endDate) => {
        if (rangeType === undefined) {
            // No filter chosen.
            return setDateRange(undefined);
        }
        if (rangeType !== RANGE_FILTER_TYPES.CUSTOM_RANGE.value) {
            return setDateRange([rangeType]);
        }
        setDateRange([rangeType, startDate, endDate]);
    };

    const onApplyHandler = () => {
        if (filterType === filterTypes.SELECT) {
            props.confirmed(filterId, Array.from(checkedValues), isExcluded);
        } else if (filterType === filterTypes.TEXT) {
            props.confirmed(filterId, textValue, isExcluded);
        } else if (filterType === filterTypes.TEXTS) {
            props.confirmed(filterId, textsValue, isExcluded);
        } else if (filterType === filterTypes.DATE_TIME) {
            if (dateRange === undefined) {
                return props.canceled();
            }
            props.confirmed(filterId, dateRange, isExcluded);
        } else if (filterType === filterTypes.RADIO) {
            props.confirmed(filterId, [radioValue], isExcluded);
        }
    };

    const onKeyPressHandler = (event) => {
        if (event.key === 'Enter' && filterType !== filterTypes.TEXTS) {
            onApplyHandler();
        }
    };

    let content = null;
    let preContent = null;
    if (filterType === filterTypes.SELECT) {
        const showSearch = filterConfig.allowSearch && filterConfig.options && filterConfig.options.length > 0;
        let search = null;
        if (showSearch) {
            search = (
                <input
                    className="block pb-2 px-4 text-base my-2 w-full border-solid border-b outline-none"
                    autoFocus
                    defaultValue={checkboxSearchText}
                    onChange={(event) => {
                        setCheckboxSearchText(event.target.value);
                    }}
                    placeholder={filterConfig.searchPlaceholder}
                />
            );
        }
        if (filterConfig.commonOptions) {
            preContent = filterConfig.commonOptions
                .filter((fc) => {
                    return !fc.hide;
                })
                .filter((fc) => {
                    return fc.label.toLowerCase().includes(checkboxSearchText.toLowerCase());
                })
                .map((fc) => (
                    <Tooltip
                        key={fc.value}
                        title={<div>{fc.label}</div>}
                        disableHoverListener={!tooltipStatus[fc.value]}
                        placement={'right'}
                        classes={{ tooltip: 'selectOverflowTooltip' }}>
                        <div className="sfcheckbox py-1">
                            <input
                                id={`${fc.label}-${fc.value}`}
                                type="checkbox"
                                name={fc.label}
                                defaultChecked={checkedValues.has(fc.value)}
                                onChange={(e) => onCheckChanged(fc.value, e.target.checked)}
                            />
                            <label className="font-normal" htmlFor={`${fc.label}-${fc.value}`} ref={fc.optionRef}>
                                {fc.label}
                                {fc.extraData && <span>{fc.extraData}</span>}
                            </label>
                        </div>
                    </Tooltip>
                ));
            if (preContent.length > 0) {
                preContent = (
                    <div>
                        {preContent}
                        <div className="border-solid border-b-2 border-slate-200 w-full px-2 py-1"></div>
                    </div>
                );
            }
        }
        content = filterConfig.options
            .filter((fc) => {
                return !fc.hide;
            })
            .filter((fc) => {
                return fc.label.toLowerCase().includes(checkboxSearchText.toLowerCase());
            })
            .map((fc) => (
                <Tooltip
                    key={fc.value}
                    title={<div>{fc.label}</div>}
                    disableHoverListener={!tooltipStatus[fc.value]}
                    placement={'right'}
                    classes={{ tooltip: 'selectOverflowTooltip' }}>
                    <div key={fc.value} className="flex gap-2 items-center">
                        <Checkbox
                            id={`${fc.label}-${fc.value}`}
                            type="checkbox"
                            name={fc.label}
                            checked={checkedValues.has(fc.value)}
                            onChange={(e) => onCheckChanged(fc.value, e.target.checked)}
                        />
                        <label className="m-0" htmlFor={`${fc.label}-${fc.value}`} ref={fc.optionRef}>
                            <span className={`font-normal ${props.darkMode ? 'text-black' : ''}`}>
                                {fc.label}
                                {fc.extraData && <span>{fc.extraData}</span>}
                            </span>
                        </label>
                    </div>
                </Tooltip>
            ));
        content = (
            <div>
                {search}
                <div className="max-h-[25vh] text-base px-3 font-normal overflow-y-auto my-1.5">
                    {preContent}
                    {content}
                </div>
            </div>
        );
    } else if (filterType === filterTypes.TEXT) {
        content = (
            <input
                className="block outline-none border-none w-40"
                maxLength={100}
                autoFocus
                defaultValue={props.value}
                onChange={onTextChanged}
                placeholder="Search..."
            />
        );
    } else if (filterType === filterTypes.TEXTS) {
        content = <TextsFilter value={value} onChange={setTextsValue} placeholder="Search..." />;
    } else if (filterType === filterTypes.DATE_TIME) {
        content = (
            <DateFilter value={value} notAllowedRangeTypes={notAllowedRangeTypes || []} changed={onDatesChanged} />
        );
    } else if (filterType === filterTypes.RADIO) {
        content = <RadioFilter value={value && value[0]} filterConfig={filterConfig} onChange={setRadioValue} />;
    }
    return (
        <div
            className={`${
                props.newDesign && 'left-[-45px] font-sf text-sf-gray-800'
            } flex flex-col absolute bg-white min-w-[300px] max-w-[400px] shadow-sm shadow-slate-500 z-50 rounded-md mx-1.5 my-1.5 ${
                openOnTop ? 'bottom-[24px]' : ''
            }`}
            onKeyPress={onKeyPressHandler}>
            <div className="flex items-center justify-between py-6 px-3.5 border-b border-solid h-6">
                <div className="font-bold mr-1.5 uppercase flex text-black">
                    <div className="text-[13px]">{`${props.header} filter`}</div>
                    {props.filterTooltip && (
                        <div className="pt-0.5">
                            <Tooltip
                                title={<div>{props.filterTooltip}</div>}
                                enterDelay={100}
                                placement={'right'}
                                classes={{ tooltip: 'moreInfoIcon-tooltip' }}>
                                <div className="moreInfoIcon"></div>
                            </Tooltip>
                        </div>
                    )}
                </div>
                {filterConfig?.allowExclude && (
                    <div>
                        <label
                            className="flex items-center align-middle"
                            htmlFor={'switch' + filterId}
                            id={'switch' + filterId}
                            onClick={() => setIsExcluded(!isExcluded)}>
                            <input
                                checked={!isExcluded}
                                onChange={() => {}}
                                className="absolute hidden cursor-pointer peer"
                                type="checkbox"
                            />
                            <div className="bg-slate-500 peer-checked:bg-sf-primary relative align-middle w-20 flex h-6 mt-1.5 rounded-xl cursor-pointer transition ease-in duration-200">
                                <span
                                    className={`text-white text-xs font-semibold  absolute top-1 ${
                                        isExcluded ? 'left-7' : 'right-7'
                                    }`}>
                                    {isExcluded ? 'Exclude' : 'Include'}
                                </span>
                            </div>
                            <div className="peer-checked:translate-x-[60px]  transition duration-400 w-4 h-4 rounded-xl bg-white mx-0.5 top-4 absolute pointer-events-none"></div>
                        </label>
                    </div>
                )}
            </div>

            {content}
            <div className="flex justify-between items-center m-0 border-solid border-t border-slate-300 px-2 py-1.5">
                <Button
                    className="rounded-3xl text-slate-400 bg-transparent py-1.5 outline-none w-24"
                    onClick={props.canceled}>
                    Cancel
                </Button>
                <Button
                    className="rounded-3xl text-slate-100 bg-sf-primary py-1.5 outline-none w-24 border border-solid"
                    onClick={onApplyHandler}>
                    Apply
                </Button>
            </div>
        </div>
    );
};

export default Filter;
